import {
  ActionIcon, Alert, Badge, Button, Card, Checkbox, Group, Modal, NumberInput, Progress, Select, SimpleGrid, Stack, Table, Text, TextInput,
  Textarea, Timeline, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DynamicFields, FIELD_TYPES, type FieldDef } from "../../components/DynamicFields";
import { fmtDate, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";

interface Stage {
  bbch_code: number; bbch_name: string; mandatory: boolean; instructions?: string; source: string; window_from?: string; window_to?: string;
  fields: FieldDef[]; template_fields: string[]; status: string; missing: string[]; predicted_date?: string | null;
  observation?: { id: number; obs_date: string; percent?: number; values: Record<string, unknown>; note?: string; observer?: string } | null;
}
interface Plan { season: number; crop_group: string; template_version: number; mandatory_total: number; mandatory_done: number; stages: Stage[] }

const COLOR: Record<string, string> = { zavrseno: "green", nepotpuno: "orange", kasni: "red", u_toku: "blue", predstoji: "gray", propusteno: "gray" };

function ObservationModal({ orchardId, season, stage, onClose }: { orchardId: number; season: number; stage: Stage; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(stage.observation?.obs_date ?? (String(season) === today.slice(0, 4) ? today : `${season}-01-01`));
  const [pct, setPct] = useState<number | string>(stage.observation?.percent ?? 50);
  const [values, setValues] = useState<Record<string, unknown>>(stage.observation?.values ?? {});
  const [note, setNote] = useState(stage.observation?.note ?? "");
  const save = useMutation({
    mutationFn: () => http.post(`/api/v1/phenology/orchards/${orchardId}/observations`, {
      bbch_code: stage.bbch_code, obs_date: date, percent: pct === "" ? null : Number(pct), values, note: note || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pheno"] }); notifications.show({ color: "green", message: t("Zapažanje upisano") }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", title: t("Provjera obaveznih podataka"), message: e.message }),
  });
  const fields = stage.fields.filter((f) => f.key !== "datum");
  return (
    <Modal opened onClose={onClose} size="lg" title={`BBCH ${stage.bbch_code} — ${stage.bbch_name}`}>
      <Stack>
        {stage.instructions && <Alert icon={<IconInfoCircle />} color="blue" variant="light">{stage.instructions}</Alert>}
        <SimpleGrid cols={2}>
          <TextInput type="date" label={t("Datum zapažanja")} required value={date} onChange={(e) => setDate(e.currentTarget.value)} />
          <NumberInput label={t("% biljaka u fazi")} min={0} max={100} value={pct} onChange={setPct} />
        </SimpleGrid>
        {fields.length > 0 && <DynamicFields defs={fields} values={values} onChange={setValues} />}
        <Textarea label={t("Napomena")} value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
        <Text size="xs" c="dimmed">{t("Polja označena zvjezdicom su obavezna po šablonu Agencije; polja dodata na nivou voćnjaka su proširenja.")}</Text>
        <Group justify="flex-end"><Button onClick={() => save.mutate()} loading={save.isPending}>{t("Sačuvaj zapažanje")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function ExtensionModal({ orchardId, stage, bbchOptions, onClose }: {
  orchardId: number; stage?: Stage; bbchOptions: { value: string; label: string }[]; onClose: () => void;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const existing = stage ? stage.fields.filter((f) => !stage.template_fields.includes(f.key)) : [];
  const [bbch, setBbch] = useState<string | null>(stage ? String(stage.bbch_code) : null);
  const [fields, setFields] = useState<FieldDef[]>(existing);
  const [note, setNote] = useState("");
  const set = (i: number, patch: Partial<FieldDef>) => setFields(fields.map((f, j) => (i === j ? { ...f, ...patch } : f)));
  const save = useMutation({
    mutationFn: () => http.put(`/api/v1/phenology/orchards/${orchardId}/extensions`, { bbch_code: Number(bbch), fields, note: note || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pheno"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} size="xl" title={stage ? t(`Proširenje faze BBCH ${stage.bbch_code}`) : t("Dodatna faza na nivou voćnjaka")}>
      <Stack>
        <Text size="sm" c="dimmed">{t("Proširenja dodaju faze i polja samo za ovaj voćnjak. Obavezne faze i polja iz šablona Agencije ne mogu se ukloniti niti mijenjati.")}</Text>
        {!stage && <Select label="BBCH" searchable data={bbchOptions} value={bbch} onChange={setBbch} required />}
        <Table>
          <Table.Thead><Table.Tr><Table.Th>{t("Ključ")}</Table.Th><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Tip")}</Table.Th>
            <Table.Th>{t("Opcije (zarezom)")}</Table.Th><Table.Th>{t("Jed.")}</Table.Th><Table.Th>{t("Obavezno")}</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {fields.map((f, i) => (
              <Table.Tr key={i}>
                <Table.Td><TextInput size="xs" value={f.key} onChange={(e) => set(i, { key: e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} /></Table.Td>
                <Table.Td><TextInput size="xs" value={f.label} onChange={(e) => set(i, { label: e.currentTarget.value })} /></Table.Td>
                <Table.Td><Select size="xs" data={FIELD_TYPES} value={f.type} onChange={(v) => set(i, { type: (v ?? "text") as FieldDef["type"] })} /></Table.Td>
                <Table.Td><TextInput size="xs" disabled={!["select", "multiselect"].includes(f.type)} value={(f.options ?? []).join(", ")}
                  onChange={(e) => set(i, { options: e.currentTarget.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Table.Td>
                <Table.Td><TextInput size="xs" w={70} value={f.unit ?? ""} onChange={(e) => set(i, { unit: e.currentTarget.value || undefined })} /></Table.Td>
                <Table.Td><Checkbox checked={!!f.required} onChange={(e) => set(i, { required: e.currentTarget.checked })} /></Table.Td>
                <Table.Td><ActionIcon color="red" variant="subtle" onClick={() => setFields(fields.filter((_, j) => j !== i))}><IconTrash size={14} /></ActionIcon></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Button variant="light" leftSection={<IconPlus size={14} />} w={200}
          onClick={() => setFields([...fields, { key: `polje_${fields.length + 1}`, label: "", type: "text" }])}>{t("Dodaj polje")}</Button>
        <TextInput label={t("Napomena")} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end"><Button onClick={() => save.mutate()} disabled={!bbch} loading={save.isPending}>{t("Sačuvaj proširenje")}</Button></Group>
      </Stack>
    </Modal>
  );
}

export function PhenologyTab({ orchardId, season }: { orchardId: number; season: number }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [obs, setObs] = useState<Stage | null>(null);
  const [ext, setExt] = useState<Stage | null | "new">(null);
  const plan = useQuery({ queryKey: ["pheno", "plan", orchardId, season], queryFn: () => http.get<Plan>(`/api/v1/phenology/orchards/${orchardId}/plan`, { season }) });
  const bbch = useQuery({ queryKey: ["bbch", plan.data?.crop_group], enabled: !!plan.data,
    queryFn: () => http.get<{ code: number; name: string }[]>("/api/v1/phenology/bbch", { crop_group: plan.data!.crop_group }) });
  const del = useMutation({
    mutationFn: (id: number) => http.del(`/api/v1/phenology/observations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pheno"] }),
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const p = plan.data;
  if (plan.error) return <Text c="red">{(plan.error as Error).message}</Text>;
  if (!p) return <Text>{t("Učitavanje...")}</Text>;
  const used = new Set(p.stages.map((s) => s.bbch_code));
  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between">
          <div>
            <Text fw={650}>{t("Fenološki plan")} {season} · {t("grupa")} {p.crop_group} · {t("šablon v")}{p.template_version}</Text>
            <Text size="sm" c="dimmed">{t("Obavezne faze")}: {p.mandatory_done}/{p.mandatory_total}</Text>
          </div>
          {can("phenology:write") && <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => setExt("new")}>{t("Dodatna faza")}</Button>}
        </Group>
        <Progress mt="xs" value={(100 * p.mandatory_done) / Math.max(1, p.mandatory_total)} />
      </Card>
      <Timeline active={-1} bulletSize={26} lineWidth={2}>
        {p.stages.map((s) => (
          <Timeline.Item key={s.bbch_code} color={COLOR[s.status]}
            bullet={<Text size="xs" fw={700} c="white">{s.bbch_code}</Text>}
            title={<Group gap="xs"><Text fw={600}>{s.bbch_name}</Text><StatusBadge status={s.status} />
              {s.mandatory ? <Badge size="xs" variant="outline">{t("obavezno")}</Badge> : <Badge size="xs" variant="outline" color="gray">{t("neobavezno")}</Badge>}
              {s.source === "prosirenje" && <Badge size="xs" color="soil" variant="light">{t("proširenje voćnjaka")}</Badge>}</Group>}>
            <Text size="xs" c="dimmed">{t("Prozor")}: {fmtDate(s.window_from)} – {fmtDate(s.window_to)}
              {s.predicted_date && <> · {t("predikcija (GDD)")}: <b>{fmtDate(s.predicted_date)}</b></>}</Text>
            {s.observation ? (
              <Card withBorder p="xs" mt={4}>
                <Group justify="space-between">
                  <Text size="sm">{fmtDate(s.observation.obs_date)} · {s.observation.percent ?? "—"}% · {s.observation.observer}</Text>
                  <Group gap={4}>
                    {can("phenology:write") && <Button size="compact-xs" variant="subtle" onClick={() => setObs(s)}>{t("Novo zapažanje")}</Button>}
                    {can("phenology:write") && <Tooltip label={t("Obriši zapažanje")}><ActionIcon size="sm" color="red" variant="subtle"
                      onClick={() => del.mutate(s.observation!.id)}><IconTrash size={14} /></ActionIcon></Tooltip>}
                  </Group>
                </Group>
                <Group gap="md" mt={4}>
                  {s.fields.filter((f) => f.key !== "datum" && s.observation!.values[f.key] !== undefined).map((f) => (
                    <Text key={f.key} size="xs"><Text span c="dimmed">{f.label}:</Text> {String(s.observation!.values[f.key])}{f.unit ? ` ${f.unit}` : ""}</Text>
                  ))}
                </Group>
                {s.missing.length > 0 && <Text size="xs" c="orange">{t("Nedostaje")}: {s.missing.join("; ")}</Text>}
              </Card>
            ) : (
              <Group gap="xs" mt={4}>
                {can("phenology:write") && <Button size="compact-sm" onClick={() => setObs(s)}>{t("Unesi zapažanje")}</Button>}
                <Text size="xs" c="dimmed">{s.fields.filter((f) => f.required).map((f) => f.label).join(", ")}</Text>
              </Group>
            )}
            {can("phenology:write") && <Button size="compact-xs" variant="subtle" color="gray" mt={2} onClick={() => setExt(s)}>{t("Proširi polja faze")}</Button>}
          </Timeline.Item>
        ))}
      </Timeline>
      {obs && <ObservationModal orchardId={orchardId} season={season} stage={obs} onClose={() => setObs(null)} />}
      {ext && <ExtensionModal orchardId={orchardId} stage={ext === "new" ? undefined : ext} onClose={() => setExt(null)}
        bbchOptions={(bbch.data ?? []).filter((b) => !used.has(b.code)).map((b) => ({ value: String(b.code), label: `${b.code} — ${b.name}` }))} />}
    </Stack>
  );
}
